/**
 * Centralized i18n-aware display labels for subscription plans.
 *
 * IMPORTANT:
 * - Internal plan IDs (free / pro / enterprise) MUST NOT be translated.
 *   They are used by the API, the database, and Stripe price mapping.
 * - This module ONLY translates UI-facing strings, via i18next.
 *
 * Always call `getPlanLabel(planId)` to render a plan name to the user.
 * Never display the raw enum values (free / pro / enterprise).
 */

import i18n from 'i18next';

/**
 * Static price snapshot — must mirror backend `config.SUBSCRIPTION_PLANS`.
 * Used only for client-side UI like preview/impact dialogs. Source of truth
 * for billing remains the backend / Stripe.
 */
export const PLAN_PRICES = {
    free: 0,
    pro: 49,
    enterprise: 199,
};

/**
 * Returns the localized display label for a given internal plan id,
 * using the current i18next language (FR/EN).
 *
 * Accepts case-insensitive ids. Falls back to a sensible default if unknown.
 *
 * @param {string|undefined|null} planId
 * @returns {string}
 */
export function getPlanLabel(planId) {
    if (!planId) return i18n.t('plans.free');
    const key = String(planId).toLowerCase();
    if (['free', 'pro', 'enterprise'].includes(key)) {
        return i18n.t(`plans.${key}`);
    }
    // Unknown plan id: render via i18n if a key exists, else keep as-is
    const candidate = i18n.t(`plans.${key}`, { defaultValue: '__missing__' });
    return candidate === '__missing__' ? planId : candidate;
}

/**
 * Reactive accessor (proxy) so that legacy `PLAN_LABELS.free` reads pick up the
 * current language at access time. Keeps backward compatibility with existing
 * call sites while being i18n-aware.
 */
export const PLAN_LABELS = new Proxy({}, {
    get(_target, prop) {
        if (typeof prop === 'string') return getPlanLabel(prop);
        return undefined;
    },
});
